import { useEffect, useRef, useState } from "react";
import type { BarLaptopConfig, BarLaptopNetwork, GoogleResultSpec } from "../scenario/types";
import { audio } from "../lib/audio";
import { SslInfoModal } from "./SslInfoModal";

/**
 * Bar Wi-Fi game (walkable bar): the counter laptop's full mini-app. Own
 * local copy of the Windows-11 lock-screen (`DeskLogin.tsx`'s `.cf-win-lock`)
 * and taskbar/desktop-icons (`Desktop.tsx`'s `.win-taskbar`/`.win-dicons`)
 * markup — this is a single linear story spanning several OS-level
 * surfaces, not a fit for either existing component's own state machine.
 * Stage sequence:
 *   lock -> desktop (Wi-Fi flyout lives here) -> google (look-alike domain
 *   pick) -> ssl warning (fires the INSTANT the site opens) -> webmail login
 *   (password hint shown here, once past the warning).
 */
type Stage = "lock" | "desktop" | "google" | "ssl" | "webmail";
type NetPhase = "idle" | "connecting" | "wrong_password" | "no_internet";

/** Same bar-height formula as `WifiKiosk.tsx`'s `SignalBars` (own local copy
 *  — that one isn't exported and this screen isn't inside a `.wk-screen`
 *  container-query context anyway, so plain px bars are used here instead). */
function SignalBars({ level }: { level: number }) {
  const bars = Math.max(1, Math.min(4, Math.round((level / 100) * 4)));
  return (
    <span className="bl-signal" aria-label={`${level}% signal`}>
      {[1, 2, 3, 4].map((b) => (
        <i key={b} className={b <= bars ? "on" : ""} style={{ height: `${3 + b * 2.6}px` }} />
      ))}
    </span>
  );
}

/** Small always-crisp "coach" card pinned top-right of the whole viewport —
 *  same corner/visual language as the beat's own storyboard `.guide` card,
 *  stacked just BELOW it. Used for contextual tips that used to be crammed
 *  inline into the (much smaller, laptop-screen-scaled) mini-app itself,
 *  e.g. "safer pick" wifi advice or "now choose a search result" — text
 *  rendered here stays a fixed, readable size regardless of how big/small
 *  the 3D laptop screen overlay itself is scaled at the current resolution. */
function BlHintCard({ children }: { children: import("react").ReactNode }) {
  return (
    <div className="bl-hintcard" role="status">
      <span className="bl-hintcard-dot" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

function LockIcon({ open }: { open: boolean }) {
  if (open) return null;
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
      <path
        d="M7 10.5V8a5 5 0 0 1 10 0v2.5M6 10.5h12a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8.5a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export function BarLaptop({
  config,
  onOutcome,
  onEnded,
  onStageChange,
  onSafeExit,
}: {
    config: BarLaptopConfig;
  onOutcome: (evt: { type: "disclosure" | "refusal"; eventId: string; evidence?: string }) => void;
  onEnded: () => void;
  /** Reports a granular UI stage key (see BARLAPTOP_STAGE_GUIDE in App.tsx)
   *  every time it changes, so the top-right guide card can stay accurate
   *  across this device's several OS-level surfaces instead of freezing on
   *  the beat's one static "Sign in, then click the Wi-Fi icon..." line for
   *  the whole episode. */
  onStageChange?: (stage: string) => void;
  /** The beat offers a safe way out: the risky network and the certificate
   *  warning lose their "Click here" hints, and the warning gets a working
   *  "Back to safety" button. */
  onSafeExit?: () => void;
}) {
  const [stage, setStage] = useState<Stage>("lock");
  const [wifiOpen, setWifiOpen] = useState(false);
  const [wifiEverOpened, setWifiEverOpened] = useState(false);
  const [connectedId, setConnectedId] = useState<string | null>(null);
  const [activeNetId, setActiveNetId] = useState<string | null>(null);
  const [netPhase, setNetPhase] = useState<NetPhase>("idle");
  const [keyInput, setKeyInput] = useState("");
  const [triedSecuredFirst, setTriedSecuredFirst] = useState(false);
  const [appDenyFlash, setAppDenyFlash] = useState(false);
  const [visitedBrowser, setVisitedBrowser] = useState(false);
  const started = useRef(false);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.innerVoiceLine) {
      void audio.prefetchSpeech(config.innerVoiceLine, {
        voice: "verse",
        lang: "en-US",
        instructions:
          "Hushed internal monologue, thinking to himself under his breath, contemplative and a little uncertain — not spoken to anyone else in the room.",
      });
    }
    if (config.sslWarning?.voiceOverLine) {
      void audio.prefetchSpeech(config.sslWarning.voiceOverLine, {
        voice: "verse",
        lang: "en-US",
        instructions:
          "Hushed internal monologue, thinking to himself under his breath, uneasy and second-guessing — not spoken to anyone else in the room.",
      });
    }
    if (started.current) return;
    started.current = true;
    audio.logon();
  }, []);

  // Derive a granular UI-stage key from the several bits of internal state
  // this device tracks, and report it upward — see the onStageChange doc
  // comment above. Recomputed on every relevant state change.
  useEffect(() => {
    if (!onStageChange) return;
    let uiStage: string;
    if (stage === "lock") uiStage = "lock";
    else if (stage === "google") uiStage = "google";
    else if (stage === "ssl") uiStage = "ssl";
    else if (stage === "webmail") uiStage = "webmail";
    else if (!connectedId) uiStage = wifiOpen && activeNetId ? "wifi_connect" : wifiOpen ? "wifi_pick" : "wifi_hint";
    else uiStage = visitedBrowser ? "browser_open" : "connect_browser";
    onStageChange(uiStage);
  }, [stage, wifiOpen, connectedId, visitedBrowser, activeNetId, onStageChange]);

  // Click-outside dismisses the flyout, matching real Windows.
  useEffect(() => {
    if (!wifiOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node) || !flyoutRef.current || flyoutRef.current.contains(target)) return;
      if (target instanceof Element && target.closest(".guide")) return;
      setWifiOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [wifiOpen]);

  // First time the flyout opens: dramatize the learner's hesitation with a
  // hushed "inner thought" line instead of leaving the pick unexplained.
  useEffect(() => {
    if (!wifiOpen || wifiEverOpened) return;
    setWifiEverOpened(true);
    if (config.innerVoiceLine) {
      void audio.speak(config.innerVoiceLine, {
        voice: "verse",
        lang: "en-US",
        instructions:
          "Hushed internal monologue, thinking to himself under his breath, contemplative and a little uncertain — not spoken to anyone else in the room.",
      });
    }
  }, [wifiOpen, wifiEverOpened, config.innerVoiceLine]);

  const openNetwork = (n: BarLaptopNetwork) => {
    if (activeNetId === n.id) {
      setActiveNetId(null);
      return;
    }
    setActiveNetId(n.id);
    setNetPhase("idle");
    setKeyInput("");
    audio.click();
  };

  const attemptConnect = (n: BarLaptopNetwork) => {
    setNetPhase("connecting");
    if (n.secured && !triedSecuredFirst && !connectedId) {
      setTriedSecuredFirst(true);
      onOutcome({
        type: "refusal",
        eventId: "tried_secured_network_first",
        evidence: `tried the confirmed/secured network (${n.ssid}) first`,
      });
    }
    window.setTimeout(() => {
      if (n.outcome === "connects") {
        setNetPhase("idle");
        setConnectedId(n.id);
        audio.click();
        onOutcome({
          type: "disclosure",
          eventId: "connected_open_trap_wifi",
          evidence: `connected to ${n.ssid}`,
        });
        window.setTimeout(() => setWifiOpen(false), 900);
      } else if (n.outcome === "wrong_password") {
        audio.errorBuzz();
        setNetPhase("wrong_password");
      } else {
        setNetPhase("no_internet");
        window.setTimeout(() => {
          setActiveNetId(null);
          setNetPhase("idle");
        }, 1700);
      }
    }, 850);
  };

  const openBrowserApp = () => {
    if (!connectedId) {
      audio.errorBuzz();
      setAppDenyFlash(true);
      window.setTimeout(() => setAppDenyFlash(false), 500);
      return;
    }
    audio.click();
    setVisitedBrowser(true);
    setStage("google");
  };

  if (stage === "lock") {
    return (
      <div className="win desklock">
        <div className="cf-win-lock">
          <div className="cf-win-login">
            <div className="cf-win-avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="54" height="54">
                <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="rgba(255,255,255,.9)" />
              </svg>
            </div>
            <div className="cf-win-user">{config.userName ?? "A. Morgan"}</div>
            <form
              className="cf-win-pwrow"
              onSubmit={(e) => {
                e.preventDefault();
                audio.logon();
                setStage("desktop");
              }}
            >
              <div
                className="cf-win-pwfield cf-blink-strong"
                role="button"
                tabIndex={0}
                aria-label="Sign in"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  audio.logon();
                  setStage("desktop");
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  audio.logon();
                  setStage("desktop");
                }}
              >
                <input
                  type="text"
                  className="cf-mask"
                  defaultValue="********"
                  placeholder="Click here to sign in"
                  aria-label="Password"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  readOnly
                  onFocus={(e) => e.currentTarget.blur()}
                />
              </div>
              <button type="submit" className="cf-win-go" aria-label="Sign in">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M5 12h13m-6-6 6 6-6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </form>
            <div className="cf-win-subtle">{config.screenTitle ?? "Personal laptop — locked"}</div>
          </div>
        </div>
      </div>
    );
  }

  if (stage === "google") {
    return (
      <BarGoogleSearch
        config={config}
        onCorrect={() => {
          audio.click();
          setStage("ssl");
        }}
      />
    );
  }

  if (stage === "ssl" || stage === "webmail") {
    return (
      <BarWebmail
        config={config}
        stage={stage}
        onBack={onSafeExit}
        onContinueAnyway={() => {
          onOutcome({
            type: "disclosure",
            eventId: "ssl_warning_bypassed",
            evidence: "clicked continue past the certificate warning",
          });
          audio.click();
          setStage("webmail");
        }}
        onSignedIn={() => {
          audio.logon();
          onEnded();
        }}
      />
    );
  }

  return (
    <>
    <div className="win bl-desktop">
      <div className="win-dicons">
        <button
          type="button"
          className={`win-dicon ${connectedId && !visitedBrowser ? "hot" : ""} ${appDenyFlash ? "bl-deny" : ""}`}
          onClick={openBrowserApp}
        >
          <span className="win-tile" style={{ background: "#4285f4" }} aria-hidden>
            {"\u{1F310}"}
          </span>
          <span className="win-dicon-label">Browser</span>
          {connectedId && !visitedBrowser && (
            <span className="win-dicon-hint" aria-hidden="true">
              Click this
            </span>
          )}
        </button>
      </div>
      <div className="win-taskbar">
        <button className="win-start" type="button" aria-label="Start">
          <span className="win-mark" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
        </button>
        <div className="win-search">
          <span className="win-search-ico" aria-hidden="true">
            {"\u{1F50D}"}
          </span>
          <span className="win-search-ph">Type here to search</span>
        </div>
        <div className="win-pinned">
          <button
            type="button"
            className={`win-pin ${connectedId && !visitedBrowser ? "pulse" : ""} ${appDenyFlash ? "bl-deny" : ""}`}
            aria-label="Mail"
            onClick={openBrowserApp}
          >
            <span className="win-tile sm" style={{ background: "#0a84ff" }}>
              {"\u2709"}
            </span>
            {connectedId && <span className="win-badge" aria-hidden="true" />}
          </button>
        </div>
        <div className="win-tray" ref={flyoutRef}>
          <span className="win-tray-ico">{"\u2227"}</span>
          <span className={`bl-wifi-hintwrap ${!wifiEverOpened && !connectedId ? "show" : ""}`}>
            <button
              type="button"
              className={`win-tray-ico bl-wifi-btn ${connectedId ? "bl-wifi-on" : "bl-wifi-blink"}`}
              aria-label="Wi-Fi icon"
              onClick={() => setWifiOpen((v) => !v)}
            >
              {"\u{1F4F6}"}
            </button>
            {!wifiEverOpened && !connectedId && <span className="bl-wifi-hint">Click here</span>}
          </span>
          <span className="win-tray-ico">{"\u{1F50A}"}</span>
          <span className="win-clock">
            <span>{config.clock ?? "9:41 PM"}</span>
          </span>

          {wifiOpen && (
            <div className="wf-flyout" role="dialog" aria-label="Wi-Fi networks">
              <div className="wf-head">
                <span>Wi-Fi</span>
                <span className="wf-toggle-on" aria-hidden />
              </div>
              <ul className="wf-list">
                {config.networks.map((n) => {
                  const active = activeNetId === n.id;
                  const connected = connectedId === n.id;
                  const isTarget = !onSafeExit && n.outcome === "connects" && !connectedId;
                  return (
                    <li key={n.id} className={`${active ? "wf-active" : ""} ${isTarget ? "wf-target" : ""}`}>
                      <button type="button" className="wf-net" onClick={() => openNetwork(n)}>
                        <span className={`wf-lock ${n.secured ? "locked" : "open"}`}>
                          <LockIcon open={!n.secured} />
                        </span>
                        <span className="wf-ssid">{n.ssid}</span>
                        <SignalBars level={n.signal} />
                        {connected && <span className="wf-connected-tag">Connected</span>}
                        {isTarget && !active && <span className="wf-net-hint">Click here</span>}
                      </button>
                      {active && !connected && (
                        <div className="wf-expand">
                          {netPhase === "connecting" && <div className="wf-status">Connecting\u2026</div>}
                          {netPhase === "idle" && n.secured && (
                            <>
                              <label className="wf-keylabel" htmlFor={`wf-key-${n.id}`}>
                                Enter the network security key
                              </label>
                              <input
                                id={`wf-key-${n.id}`}
                                type="text"
                                className="wf-key-input cf-mask"
                                value={keyInput}
                                onChange={(e) => setKeyInput(e.target.value)}
                                autoComplete="off"
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-form-type="other"
                                spellCheck={false}
                              />
                              <div className="wf-actions">
                                <button type="button" className="btn wf-connect" onClick={() => attemptConnect(n)}>
                                  Connect
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={() => setActiveNetId(null)}>
                                  Cancel
                                </button>
                              </div>
                            </>
                          )}
                          {netPhase === "idle" && !n.secured && (
                            <div className="wf-actions">
                              <label className="wf-auto">
                                <input type="checkbox" defaultChecked disabled /> Connect automatically
                              </label>
                              <button type="button" className="btn wf-connect" onClick={() => attemptConnect(n)}>
                                Connect
                              </button>
                            </div>
                          )}
                          {netPhase === "wrong_password" && (
                            <>
                              <div className="wf-error">Network security key is incorrect. Try entering the password again.</div>
                              <div className="wf-actions">
                                <button type="button" className="btn wf-connect" onClick={() => setNetPhase("idle")}>
                                  Try again
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={() => setActiveNetId(null)}>
                                  Cancel
                                </button>
                              </div>
                            </>
                          )}
                          {netPhase === "no_internet" && <div className="wf-error wf-error--soft">No Internet, secured</div>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
    {wifiOpen && config.wifiTip && <BlHintCard>{config.wifiTip}</BlHintCard>}
    </>
  );
}

/** Fake Google search results — the learner has to tell the real work-mail
 *  domain apart from one-or-more look-alike/typosquat decoys before the site
 *  even loads. Picking a decoy explains the attack inline, then lets the
 *  learner try again rather than dead-ending the story. */
type GooglePhase = "typing" | "searching" | "results";

function BarGoogleSearch({
  config,
  onCorrect,
}: {
  config: BarLaptopConfig;
  onCorrect: () => void;
}) {
  const [wrongPick, setWrongPick] = useState<GoogleResultSpec | null>(null);
  const [phase, setPhase] = useState<GooglePhase>("typing");
  const [typed, setTyped] = useState("");
  const startedTyping = useRef(false);

  // Type the query out letter-by-letter, then "click search", THEN reveal
  // results — instead of dumping the finished query + results on screen at
  // once, so the moment reads as the learner actively searching rather than
  // a pre-filled static page.
  useEffect(() => {
    if (startedTyping.current) return;
    startedTyping.current = true;
    const query = config.googleSearch.query;
    let cancelled = false;
    let i = 0;
    const typeNext = () => {
      if (cancelled) return;
      i += 1;
      setTyped(query.slice(0, i));
      if (i < query.length) {
        window.setTimeout(typeNext, 34 + Math.random() * 55);
      } else {
        window.setTimeout(() => {
          if (cancelled) return;
          audio.click();
          setPhase("searching");
          window.setTimeout(() => {
            if (!cancelled) setPhase("results");
          }, 700);
        }, 500);
      }
    };
    const t = window.setTimeout(typeNext, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [config.googleSearch.query]);

  const pick = (r: GoogleResultSpec) => {
    if (r.correct) {
      onCorrect();
      return;
    }
    audio.errorBuzz();
    setWrongPick(r);
  };

  return (
    <>
    <div className="win bl-desktop bl-browser-win">
      <div className="bl-browser bl-google">
        <div className="bl-urlbar ok">
          <span className="bl-url-lock" aria-hidden>
            {"\u{1F512}"}
          </span>
          <span className="bl-url">google.com/search</span>
        </div>
        {wrongPick ? (
          <div className="bl-lookalike-explain">
            <div className="bl-certerr-ico" aria-hidden>
              {"\u26A0"}
            </div>
            <h3>That wasn't the real site</h3>
            <p className="bl-lookalike-domain">{wrongPick.url}</p>
            <p>{config.googleSearch.lookalikeExplainer}</p>
            <button type="button" className="btn bl-certerr-btn" onClick={() => setWrongPick(null)}>
              Back to search results
            </button>
          </div>
        ) : (
          <div className="bl-google-body">
            <div className={`bl-google-searchbar ${phase === "typing" ? "bl-typing" : ""}`}>
              <span aria-hidden>{"\u{1F50D}"}</span>
              <span className="bl-google-typed">
                {typed}
                {phase === "typing" && <span className="bl-typecaret" aria-hidden />}
              </span>
              {phase !== "typing" && (
                <span className="bl-google-searchbtn" aria-hidden>
                  {"\u{1F50E}"}
                </span>
              )}
            </div>
            {phase === "searching" && <div className="bl-google-loading">Searching\u2026</div>}
            {phase === "results" && (
              <div className="bl-google-results">
                {config.googleSearch.results.map((r) => (
                  <button type="button" key={r.id} className="bl-gresult" onClick={() => pick(r)}>
                    <span className="bl-gresult-url">
                      <span className="bl-gresult-lock" aria-hidden>
                        {"\u{1F512}"}
                      </span>
                      {r.url}
                    </span>
                    <span className="bl-gresult-title">{r.title}</span>
                    <span className="bl-gresult-snippet">{r.snippet}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {phase === "results" && !wrongPick && (
      <BlHintCard>Pick the search result that matches the real company domain to continue.</BlHintCard>
    )}
    </>
  );
}

/** SSL/certificate warning (fires the instant the real site "opens", before
 *  any login) followed by the webmail sign-in form with a copy-paste
 *  password hint — a self-contained fake "browser" chrome (own `bl-*`
 *  classes, no container-query coupling to `WifiKiosk.tsx`'s `.wk-screen`). */
export function BarWebmail({
  config,
  stage,
  onSignedIn,
  onContinueAnyway,
  onBack,
}: {
  config: BarLaptopConfig;
  stage: "ssl" | "webmail";
  onSignedIn: () => void;
  onContinueAnyway: () => void;
  onBack?: () => void;
}) {
  const [pass, setPass] = useState("");
  const [copied, setCopied] = useState(false);
  const [showSslInfo, setShowSslInfo] = useState(false);
  const spokeVoiceOver = useRef(false);

  // The instant the SSL warning first appears, dramatize the moment of
  // hesitation with a short off-voice reaction instead of a silent screen.
  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.sslWarning?.voiceOverLine) {
      void audio.prefetchSpeech(config.sslWarning.voiceOverLine, {
        voice: "verse",
        lang: "en-US",
        instructions:
          "Hushed internal monologue, thinking to himself under his breath, uneasy and second-guessing — not spoken to anyone else in the room.",
      });
    }
    if (stage !== "ssl" || spokeVoiceOver.current) return;
    spokeVoiceOver.current = true;
    if (config.sslWarning.voiceOverLine) {
      void audio.speak(config.sslWarning.voiceOverLine, {
        voice: "verse",
        lang: "en-US",
        instructions:
          "Hushed internal monologue, thinking to himself under his breath, uneasy and second-guessing — not spoken to anyone else in the room.",
      });
    }
  }, [stage, config.sslWarning.voiceOverLine]);

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(config.webmail.password);
    } catch {
      /* clipboard API unavailable — fall back to just filling the field */
    }
    setPass(config.webmail.password);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="win bl-desktop bl-browser-win">
      <div className="bl-browser">
        <div className={`bl-urlbar ${stage === "ssl" ? "bad" : "ok"}`}>
          <span className={`bl-url-lock ${stage === "ssl" ? "warn" : ""}`} aria-hidden>
            {stage === "ssl" ? "\u26A0" : "\u{1F512}"}
          </span>
          <span className="bl-url">{config.webmail.url}</span>
          {stage === "ssl" && <span className="bl-url-tag">Not secure</span>}
        </div>

        {stage === "ssl" ? (
          <div className="bl-certerr">
            <div className="bl-certerr-ico" aria-hidden>
              {"\u26A0"}
            </div>
            <h3>{config.sslWarning.title}</h3>
            <p>{config.sslWarning.body}</p>
            {config.sslWarning.errorCode && <div className="bl-certerr-code">{config.sslWarning.errorCode}</div>}
            {onBack && (
              <button type="button" className="btn bl-certerr-btn" onClick={onBack}>
                Back to safety
              </button>
            )}
            <span className={`bl-certerr-hintwrap ${onBack ? "" : "show"}`}>
              <button type="button" className={`btn ${onBack ? "btn-ghost bl-certerr-proceed" : "bl-certerr-btn"}`} onClick={onContinueAnyway}>
                {config.sslWarning.continueLabel ?? "Continue to this site (not recommended)"}
              </button>
              {!onBack && <span className="bl-certerr-hint">Click here</span>}
            </span>
            <button type="button" className="bl-certerr-info" onClick={() => setShowSslInfo(true)}>
              <span className="bl-certerr-info-ico" aria-hidden>
                {"\u24D8"}
              </span>
              {config.sslWarning.infoLabel ?? "What does this warning mean?"}
            </button>
          </div>
        ) : (
          <div className="bl-page">
            {config.webmail.password && (
              <div className={`bl-pwhint ${!pass ? "cf-blink-strong" : ""}`}>
                <span className="bl-pwhint-label">Password reminder</span>
                <code className="bl-pwhint-value">{config.webmail.password}</code>
                <button type="button" className="bl-pwhint-copy" onClick={copyPassword}>
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}
            <div className="bl-brand">{config.webmail.brand}</div>
            <h3>{config.webmail.headline ?? "Sign in to your work email"}</h3>
            <div className="bl-form">
              <label>
                <span>Email</span>
                <input value={config.webmail.userEmail} readOnly />
              </label>
              <label>
                <span>Password</span>
                <input
                  type="text"
                  className="cf-mask"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="Password"
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  spellCheck={false}
                />
              </label>
            </div>
            <span className="bl-signin-hintwrap show">
              <button type="button" className="btn bl-webmail-signin" onClick={onSignedIn}>
                Sign in
              </button>
              <span className="bl-signin-hint">Click here</span>
            </span>
          </div>
        )}
      </div>
      {showSslInfo && <SslInfoModal onClose={() => setShowSslInfo(false)} />}
    </div>
  );
}
